import { SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { HealthcareProfessionalTypeSelect } from "@/components/Selects/HealthcareProfessionalTypeSelect";
import { SpecialtiesSelectObject } from "@/components/Selects/SpecialtiesSelectObject";
import { USStateSelect } from "@/components/Selects/USStateSelect";
import { EmploymentTypeSelect } from "@/components/Selects/EmploymentTypeSelect";
import { PracticeSettingSelect } from "@/components/Selects/PracticeSettingSelect";
import { EmploymentType, PracticeSetting } from "@api/shared-runtime/enums";

export function JobSearchFilters({
  professionalTypeFilter,
  setProfessionalTypeFilter,
  specialtyFilter,
  setSpecialtyFilter,
  stateFilter,
  setStateFilter,
  practiceSettingFilter,
  setPracticeSettingFilter,
  employmentTypeFilter,
  setEmploymentTypeFilter,
  includeRemote,
  setIncludeRemote,
  showRejectedJobs,
  setShowRejectedJobs,
  includeExternalJobs,
  setIncludeExternalJobs,
  activeFilterCount,
  onReset,
  hideTitle = false,
}: {
  professionalTypeFilter: { name: string; id: string } | undefined;
  setProfessionalTypeFilter: (
    value: { name: string; id: string } | undefined,
  ) => void;
  specialtyFilter: { name: string; id: string }[];
  setSpecialtyFilter: (value: { name: string; id: string }[]) => void;
  stateFilter: string[];
  setStateFilter: (value: string[]) => void;
  practiceSettingFilter: PracticeSetting[];
  setPracticeSettingFilter: (value: PracticeSetting[]) => void;
  employmentTypeFilter: EmploymentType[];
  setEmploymentTypeFilter: (value: EmploymentType[]) => void;
  includeRemote: boolean;
  setIncludeRemote: (value: boolean) => void;
  showRejectedJobs: boolean;
  setShowRejectedJobs: (value: boolean) => void;
  includeExternalJobs: boolean;
  setIncludeExternalJobs: (value: boolean) => void;
  activeFilterCount: number;
  onReset: () => void;
  hideTitle?: boolean;
}) {
  return (
    <div>
      {(!hideTitle || activeFilterCount > 0) && (
        <div className="flex items-center justify-between mb-5">
          {hideTitle ? (
            <div />
          ) : (
            <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <SlidersHorizontal className="h-4 w-4 text-slate-400" />
              Filters
              {activeFilterCount > 0 && (
                <span className="rounded-full bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground">
                  {activeFilterCount}
                </span>
              )}
            </h2>
          )}
          {activeFilterCount > 0 && (
            <Button
              onClick={onReset}
              variant="ghost"
              size="sm"
              className="text-slate-500 hover:text-slate-900"
            >
              Clear filters
            </Button>
          )}
        </div>
      )}

      <div className="space-y-5">
        {/* Professional Type Filter */}
        <div className="space-y-2">
          <Label className="text-sm font-medium text-slate-700">
            Professional Type
          </Label>
          <HealthcareProfessionalTypeSelect
            isMulti={false}
            value={professionalTypeFilter}
            onChange={(value) =>
              setProfessionalTypeFilter(value as { name: string; id: string })
            }
            placeholder="Select type"
          />
        </div>

        {/* Specialty Filter */}
        <div className="space-y-2">
          <Label className="text-sm font-medium text-slate-700">
            Specialty
          </Label>
          <SpecialtiesSelectObject
            value={specialtyFilter}
            // @ts-expect-error - existing dynamic route typing
            onChange={(value) => setSpecialtyFilter(value || [])}
            placeholder="Choose specialties"
            isMulti={true}
          />
        </div>

        {/* Location Filter */}
        <div className="space-y-2">
          <Label className="text-sm font-medium text-slate-700">Location</Label>
          <USStateSelect
            value={stateFilter}
            onChange={(value) => setStateFilter(value as string[])}
            multiple={true}
            placeholder="Select states"
          />
        </div>

        {/* Practice Setting Filter */}
        <div className="space-y-2">
          <Label className="text-sm font-medium text-slate-700">
            Practice Setting
          </Label>
          <PracticeSettingSelect
            value={practiceSettingFilter}
            onChange={setPracticeSettingFilter}
            placeholder="Select settings"
          />
        </div>

        {/* Employment Type Filter */}
        <div className="space-y-2">
          <Label className="text-sm font-medium text-slate-700">
            Employment Type
          </Label>
          <EmploymentTypeSelect
            value={employmentTypeFilter}
            onChange={setEmploymentTypeFilter}
            placeholder="Choose employment type"
          />
        </div>
      </div>

      {/* Toggle Options */}
      <div className="pt-4 mt-5 border-t border-slate-100">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">
          Include
        </p>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm text-slate-700">Remote jobs</Label>
            <Switch
              checked={includeRemote}
              onCheckedChange={setIncludeRemote}
              className="data-[state=checked]:bg-primary"
            />
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-sm text-slate-700">External jobs</Label>
            <Switch
              checked={includeExternalJobs}
              onCheckedChange={setIncludeExternalJobs}
              className="data-[state=checked]:bg-primary"
            />
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-sm text-slate-700">Jobs I passed on</Label>
            <Switch
              checked={showRejectedJobs}
              onCheckedChange={setShowRejectedJobs}
              className="data-[state=checked]:bg-primary"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
